import React, { useMemo } from 'react';
import {
  buildHourOptions,
  buildMinuteQuarterOptions,
  joinTimeParts,
  splitTimeParts,
} from '../lib/weeklyActivityFormUtils';

const selectStyle = {
  flex: 1,
  padding: '8px 10px',
  fontSize: 14,
  border: '1px solid #ccc',
  borderRadius: 6,
  boxSizing: 'border-box',
};

/** 時／分分開選擇，分鐘僅 00/15/30/45；value 為 HH:mm
 *  @param {string[]|null} [allowedValues] 若傳入，僅允許這些 HH:mm（目前值即使不在清單也會顯示）
 */
export default function QuarterHourTimeInput({
  value,
  onChange,
  disabled = false,
  allowClear = false,
  style,
  allowedValues = null,
}) {
  const allowedSet = useMemo(() => {
    if (!Array.isArray(allowedValues)) return null;
    const list = allowedValues.filter(Boolean);
    if (!list.length) return null;
    return new Set(list);
  }, [allowedValues]);

  const { hour, minute } = splitTimeParts(value);

  const hourOptions = useMemo(() => {
    const all = buildHourOptions();
    if (!allowedSet) return all;
    const hours = new Set([...allowedSet].map((t) => String(t).slice(0, 2)));
    if (hour) hours.add(hour);
    return all.filter((o) => hours.has(o.value));
  }, [allowedSet, hour]);

  const minuteOptions = useMemo(() => {
    const all = buildMinuteQuarterOptions();
    if (!allowedSet) return all;
    if (!hour) return all;
    const mins = new Set(
      [...allowedSet]
        .filter((t) => String(t).startsWith(`${hour}:`))
        .map((t) => String(t).slice(3, 5)),
    );
    if (minute) mins.add(minute);
    return all.filter((o) => mins.has(o.value));
  }, [allowedSet, hour, minute]);

  const handleHour = (e) => {
    const h = e.target.value;
    if (!h) {
      onChange('');
      return;
    }
    let next = joinTimeParts(h, minute || '00');
    if (allowedSet && next && !allowedSet.has(next)) {
      const first = [...allowedSet].find((t) => String(t).startsWith(`${h}:`));
      next = first || '';
    }
    onChange(next);
  };

  const handleMinute = (e) => {
    const m = e.target.value;
    if (!m) {
      if (allowClear) onChange('');
      return;
    }
    if (!hour) {
      onChange(joinTimeParts('00', m));
      return;
    }
    const next = joinTimeParts(hour, m);
    if (allowedSet && next && !allowedSet.has(next) && next !== value) return;
    onChange(next);
  };

  const handleClear = () => onChange('');

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', ...style }}>
      <select
        value={hour}
        onChange={handleHour}
        disabled={disabled}
        style={selectStyle}
        aria-label="時"
      >
        {!hour && <option value="">時</option>}
        {hourOptions.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <span style={{ color: '#666', flexShrink: 0 }}>:</span>
      <select
        value={minute}
        onChange={handleMinute}
        disabled={disabled}
        style={selectStyle}
        aria-label="分"
      >
        {!minute && <option value="">分</option>}
        {minuteOptions.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {allowClear && value && !disabled && (
        <button
          type="button"
          onClick={handleClear}
          style={{
            border: 'none', background: 'none', color: '#888', cursor: 'pointer',
            fontSize: 13, padding: '0 4px',
          }}
          aria-label="清除"
        >
          ✕
        </button>
      )}
    </div>
  );
}
